/**
 * Search Card Style Titles: the DOM half (repo root `Search Card Style Titles.md`
 * §6.2, §6.3). Reads the cards of one surface, asks `cardTitlesFor`, and writes
 * only what differs.
 *
 * Every pass works from a card's FULL title, remembered on the element, never
 * from the text it shows: a second pass must not take "(Womens)" off "Jacket
 * (Womens)", and a later page of results may need a colour put back.
 */

import { cardTitlesFor, styleCardTitle } from "./cardTitles.js";

/** Holds a card's full title. Liquid writes it on the cards it shortened (product-block.liquid). */
export const FULL_TITLE_ATTR = "data-wp-full-title";

/** Marks a card showing the style name; styles.css hides its SKU line (owner Q3, 2026-10-20). */
export const STYLE_CARD_CLASS = "wp-style-card";

/**
 * Searchanise gives every product `<li>` one `snize-product-tag-<tag>` class per
 * product tag (spaces to `-`, other characters and leading digits dropped, lower
 * case). Scanner tags a style's representative colour `StylePrimaryDisplay`.
 */
const SEARCHANISE_PRIMARY_CLASS = "snize-product-tag-styleprimarydisplay";

/** The Searchanise surfaces: the results page and the as-you-type dropdown (desktop, mobile). */
const SEARCHANISE_ROOT_SELECTOR = ".snize-search-results, .snize-ac-results, .snize-ac-results-mobile";

interface CardRef {
  /** The element that carries the card's state class and aria-label. */
  cardEl: HTMLElement;
  /** The element whose text is the title. */
  titleEl: HTMLElement;
  fullTitle: string;
  eligible: boolean;
}

/**
 * The full title of a Searchanise card. Remembered on first sight; re-read when
 * the text is neither the remembered title nor its short form, because then
 * Searchanise has written a different product into the element.
 */
function searchaniseFullTitle(titleEl: HTMLElement): string {
  const text = (titleEl.textContent ?? "").trim();
  const remembered = titleEl.getAttribute(FULL_TITLE_ATTR);
  if (remembered !== null && (text === remembered || text === styleCardTitle(remembered))) return remembered;
  titleEl.setAttribute(FULL_TITLE_ATTR, text);
  return text;
}

/** Writes each card's title, state class and aria-labels, touching only what differs. */
function applyCards(cards: readonly CardRef[]): void {
  const titles = cardTitlesFor(cards.map((card) => ({ fullTitle: card.fullTitle, eligible: card.eligible })));
  cards.forEach((card, index) => {
    const title = titles[index];
    if ((card.titleEl.textContent ?? "").trim() !== title) card.titleEl.textContent = title;

    const shortened = title !== card.fullTitle;
    if (card.cardEl.classList.contains(STYLE_CARD_CLASS) !== shortened) {
      card.cardEl.classList.toggle(STYLE_CARD_CLASS, shortened);
    }

    // The dropdown row is `role=button` and labelled on the <li>; links carry their own.
    const shortTitle = styleCardTitle(card.fullTitle);
    const labelled = [card.cardEl, ...Array.from(card.cardEl.querySelectorAll<HTMLElement>("a[aria-label]"))];
    for (const element of labelled) {
      const label = element.getAttribute("aria-label");
      if (label === null || label === title) continue;
      if (label === card.fullTitle || label === shortTitle) element.setAttribute("aria-label", title);
    }
  });
}

/** One Searchanise surface: every product card under `root`. */
export function applyToSearchaniseRoot(root: ParentNode): void {
  const cards: CardRef[] = [];
  root.querySelectorAll<HTMLElement>("li.snize-product").forEach((cardEl) => {
    const titleEl = cardEl.querySelector<HTMLElement>(".snize-title");
    if (titleEl === null) return;
    cards.push({
      cardEl,
      titleEl,
      fullTitle: searchaniseFullTitle(titleEl),
      eligible: cardEl.classList.contains(SEARCHANISE_PRIMARY_CLASS),
    });
  });
  if (cards.length > 0) applyCards(cards);
}

/** Every Searchanise surface on the page, each judged on its own. */
export function applyToAllSearchaniseRoots(): void {
  document.querySelectorAll(SEARCHANISE_ROOT_SELECTOR).forEach((root) => applyToSearchaniseRoot(root));
}

/**
 * The theme's product cards (`snippets/product-block.liquid`). Liquid has already
 * shortened the eligible ones and left their full title in `data-wp-full-title`;
 * this pass only puts the colour back where two cards on the page now read the
 * same (ST-1c).
 */
export function applyToThemeCards(root: ParentNode): void {
  const cards: CardRef[] = [];
  root.querySelectorAll<HTMLElement>(".product-block__title-link").forEach((link) => {
    const remembered = link.getAttribute(FULL_TITLE_ATTR);
    cards.push({
      cardEl: link,
      titleEl: link,
      fullTitle: remembered ?? (link.textContent ?? "").trim(),
      eligible: remembered !== null,
    });
  });
  if (cards.some((card) => card.eligible)) applyCards(cards);
}
